import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import msg from '@salesforce/retail-react-app/app/components/display-price/messages'

const CurrentPrice = ({
    labelForA11y,
    price,
    as: Tag = 'span',
    isRange = false,
    currency,
    ...extraProps
}) => {
    const intl = useIntl()
    const currentPriceText = intl.formatNumber(price, {style: 'currency', currency})
    return (
        <>
            {isRange ? (
                <Tag
                    {...extraProps}
                    aria-live="polite"
                    aria-label={intl.formatMessage(msg.ariaLabelCurrentPriceWithRange, {
                        currentPrice: currentPriceText
                    })}
                >
                    {intl.formatMessage(msg.currentPriceWithRange, {
                        currentPrice: currentPriceText
                    })}
                </Tag>
            ) : (
                <Tag
                    {...extraProps}
                    aria-label={intl.formatMessage(msg.ariaLabelCurrentPrice, {
                        currentPrice: currentPriceText
                    })}
                >
                    {currentPriceText}
                </Tag>
            )}
            <span className="sr-only" aria-live="polite" aria-atomic={true}>
                {labelForA11y}
                {isRange
                    ? intl.formatMessage(msg.ariaLabelCurrentPriceWithRange, {
                          currentPrice: currentPriceText
                      })
                    : intl.formatMessage(msg.ariaLabelCurrentPrice, {
                          currentPrice: currentPriceText
                      })}
            </span>
        </>
    )
}

CurrentPrice.propTypes = {
    labelForA11y: PropTypes.string,
    price: PropTypes.number.isRequired,
    as: PropTypes.string,
    isRange: PropTypes.bool,
    currency: PropTypes.string.isRequired
}

const ListPrice = ({labelForA11y, price, isRange = false, currency, ...extraProps}) => {
    const intl = useIntl()
    const listPriceText = intl.formatNumber(price, {style: 'currency', currency})
    return (
        <>
            {isRange ? (
                <s
                    {...extraProps}
                    aria-label={intl.formatMessage(msg.ariaLabelListPriceWithRange, {
                        listPrice: listPriceText
                    })}
                >
                    {intl.formatMessage(msg.listPriceWithRange, {listPrice: listPriceText})}
                </s>
            ) : (
                <s
                    {...extraProps}
                    aria-label={intl.formatMessage(msg.ariaLabelListPrice, {
                        listPrice: listPriceText
                    })}
                >
                    {listPriceText}
                </s>
            )}
            <span className="sr-only" aria-live="polite" aria-atomic={true}>
                {labelForA11y}
                {isRange
                    ? intl.formatMessage(msg.ariaLabelListPriceWithRange, {
                          listPrice: listPriceText
                      })
                    : intl.formatMessage(msg.ariaLabelListPrice, {listPrice: listPriceText})}
            </span>
        </>
    )
}

ListPrice.propTypes = {
    labelForA11y: PropTypes.string,
    price: PropTypes.number.isRequired,
    isRange: PropTypes.bool,
    currency: PropTypes.string.isRequired
}

const DisplayPrice = ({
    labelForA11y,
    priceData,
    currency,
    quantity = 1,
    currentPriceProps = {},
    listPriceProps = {}
}) => {
    const {listPrice, currentPrice, isASet, isMaster, isOnSale, isRange} = priceData
    const renderCurrentPrice = (isRange) => (
        <CurrentPrice
            labelForA11y={labelForA11y}
            price={currentPrice * quantity}
            as="b"
            currency={currency}
            isRange={isRange}
            {...currentPriceProps}
        />
    )

    const renderListPrice = (isRange) =>
        listPrice && (
            <ListPrice
                labelForA11y={labelForA11y}
                currency={currency}
                price={listPrice * quantity}
                isRange={isRange}
                {...listPriceProps}
            />
        )

    const renderPriceSet = (isRange) => (
        <>
            {renderCurrentPrice(isRange)} {isOnSale && renderListPrice(isRange)}
        </>
    )

    if (isASet) return renderCurrentPrice(true)
    if (isMaster) return renderPriceSet(isRange)
    return <div>{renderPriceSet(false)}</div>
}

DisplayPrice.propTypes = {
    priceData: PropTypes.shape({
        currentPrice: PropTypes.number.isRequired,
        isOnSale: PropTypes.bool.isRequired,
        listPrice: PropTypes.number,
        isASet: PropTypes.bool,
        isMaster: PropTypes.bool,
        isRange: PropTypes.bool
    }),
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    currentPriceProps: PropTypes.object,
    listPriceProps: PropTypes.object,
    quantity: PropTypes.number
}

export default DisplayPrice
